import SwiperCore, { Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useEffect } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { ButtonTheme } from 'builder/components/Button'
import { useConfig } from 'builder/hooks/useConfig'
import { actions, selectors } from 'builder/modules/billing'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useI18n } from 'builder/hooks/useI18n'
import { getDayOfWeek } from 'builder/utils/formatDate'
import {
  Container,
  Title,
  Content,
  Description,
  ItemsContainer,
  Item,
  ItemContainer,
  ButtonsContainer,
  PremiumButton,
  Header,
  Icon,
  SkipButton,
  Subtitle,
  SliderContent,
  SliderContainer,
  CustomPagination,
  AnimationImage,
  Screen,
  TextContainer,
  Support,
} from './styles'
import DOCUMENT from './assets/icons/document.svg'
import DOCUMENTS from './assets/icons/documents.svg'
import MONEY from './assets/icons/money.svg'
import PEOPLE from './assets/icons/people.svg'
import ONE_DESKTOP from './assets/animation/desktop/1.png'
import TWO_DESKTOP from './assets/animation/desktop/2.png'
import THREE_DESKTOP from './assets/animation/desktop/3.png'
import FOUR_DESKTOP from './assets/animation/desktop/4.png'
import FIVE_DESKTOP from './assets/animation/desktop/5.png'
import SIX_DESKTOP from './assets/animation/desktop/6.png'
import SEVEN_DESKTOP from './assets/animation/desktop/7.png'
import ONE_TABLET from './assets/animation/tablet/1.png'
import TWO_TABLET from './assets/animation/tablet/2.png'
import THREE_TABLET from './assets/animation/tablet/3.png'
import FOUR_TABLET from './assets/animation/tablet/4.png'
import FIVE_TABLET from './assets/animation/tablet/5.png'
import SIX_TABLET from './assets/animation/tablet/6.png'
import SEVEN_TABLET from './assets/animation/tablet/7.png'
import ONE_PHONE from './assets/animation/phone/1.png'
import TWO_PHONE from './assets/animation/phone/2.png'
import THREE_PHONE from './assets/animation/phone/3.png'
import FOUR_PHONE from './assets/animation/phone/4.png'
import FIVE_PHONE from './assets/animation/phone/5.png'
import SIX_PHONE from './assets/animation/phone/6.png'
import SEVEN_PHONE from './assets/animation/phone/7.png'

const ITEMS = (t: (key: string) => string) => [
  {
    icon: DOCUMENTS,
    text: t('builder.yearly_offer.items.one'),
  },
  {
    icon: DOCUMENT,
    text: t('builder.yearly_offer.items.two'),
  },
  {
    icon: PEOPLE,
    text: t('builder.yearly_offer.items.three'),
  },
  {
    icon: MONEY,
    text: t('builder.yearly_offer.items.four'),
  },
]

const IMAGES = [
  {
    desktopSrc: ONE_DESKTOP,
    tabletSrc: ONE_TABLET,
    phoneSrc: ONE_PHONE,
  },
  {
    desktopSrc: TWO_DESKTOP,
    tabletSrc: TWO_TABLET,
    phoneSrc: TWO_PHONE,
  },
  {
    desktopSrc: THREE_DESKTOP,
    tabletSrc: THREE_TABLET,
    phoneSrc: THREE_PHONE,
  },
  {
    desktopSrc: FOUR_DESKTOP,
    tabletSrc: FOUR_TABLET,
    phoneSrc: FOUR_PHONE,
  },
  {
    desktopSrc: FIVE_DESKTOP,
    tabletSrc: FIVE_TABLET,
    phoneSrc: FIVE_PHONE,
  },
  {
    desktopSrc: SIX_DESKTOP,
    tabletSrc: SIX_TABLET,
    phoneSrc: SIX_PHONE,
  },
  {
    desktopSrc: SEVEN_DESKTOP,
    tabletSrc: SEVEN_TABLET,
    phoneSrc: SEVEN_PHONE,
  },
]

SwiperCore.use([Navigation])

export const OfferView = () => {
  const navigate = useNavigate()
  const config = useConfig()
  const isYearlyOfferValid = config?.features.isYearlyOfferValid
  const dispatch = useDispatch()
  const yearlyOfferPlan = useTypedSelector(selectors.yearlyOfferPlan)
  const { isPhone, isTablet } = useMediaQueries()
  const isCareerIo =
    window.location.hostname === 'staging.career.io' ||
    window.location.hostname === 'career.io' ||
    window.location.hostname === 'careerio.topresume.com'
  const { i18n } = useI18n()

  useEffect(() => {
    dispatch(actions.fetchYearlyOfferPlan())
  }, [dispatch])

  useEffect(() => {
    if (isYearlyOfferValid) {
      trackInternalEvent('yearly_promo_page_shown')
    }
  }, [])

  if (!isYearlyOfferValid) {
    window.location.replace(`/app/billing/plans`)
    return null
  }

  const yearlyAmount = yearlyOfferPlan?.recurringAmount
  const monthlyAmount = (yearlyAmount || 0) / 12
  const dayOfWeek = i18n.t(`builder.days_of_week.${getDayOfWeek()}`)

  return (
    <Screen>
      <Container>
        <Header>{i18n.t('builder.yearly_offer.welcome')}</Header>
        <Content>
          <Description>
            <TextContainer className="title">
              <Title>{i18n.t('builder.yearly_offer.get', { day: dayOfWeek })}</Title>
              <Title className="amount">{i18n.t('builder.yearly_offer.75_off')}</Title>
            </TextContainer>
            <Subtitle>
              {i18n.t('builder.yearly_offer.pay_amount_with_cancel', {
                monthly_amount: (monthlyAmount / 100).toFixed(1),
                yearly_amount: (yearlyAmount || 0) / 100,
              })}
            </Subtitle>
            <Subtitle className="mobile">
              {i18n.t('builder.yearly_offer.pay_amount', {
                monthly_amount: (monthlyAmount / 100).toFixed(1),
                yearly_amount: (yearlyAmount || 0) / 100,
              })}
            </Subtitle>
            <Subtitle className="mobile">{i18n.t('builder.yearly_offer.cancel_anytime')}</Subtitle>
            <ItemsContainer>
              {ITEMS(i18n.t).map((item, index) => (
                <ItemContainer key={item.text}>
                  <Icon src={item.icon} />
                  <Item className={index === 3 ? 'bold' : ''}>{item.text}</Item>
                </ItemContainer>
              ))}
            </ItemsContainer>
          </Description>
        </Content>
        <ButtonsContainer>
          <PremiumButton
            onClick={() => {
              trackInternalEvent('promo_acceptance', {
                promo: 'yearly_discount',
                where: 'promo_page',
              })
              window.location.href = '/app/billing/payment?plan=resumeio-sub-yearly'
            }}
          >
            {i18n.t('builder.yearly_offer.activate')}
          </PremiumButton>
          <SkipButton theme={ButtonTheme.ghost} onClick={() => navigate('/billing/plans')}>
            {i18n.t('builder.yearly_offer.not_today')}
          </SkipButton>
        </ButtonsContainer>
        <TextContainer className="support">
          <Support>{i18n.t('builder.yearly_offer.cancel_email')}</Support>
          <Support className="email">support@{isCareerIo ? 'career' : 'resume'}.io</Support>
        </TextContainer>
      </Container>
      <SliderContainer>
        <CustomPagination className="my-custom-pagination" />
        <SliderContent>
          <Swiper
            initialSlide={0}
            slidesPerView={1}
            loop
            autoplay
            speed={300}
            pagination={{
              el: '.my-custom-pagination',
              type: 'custom',
              renderCustom(swiper, current) {
                let boxes = ''
                for (let i = 1; i <= current; i++) {
                  boxes += '<div class="box"></div>'
                }
                return `<div style="display: flex; gap: 3px">${boxes}</div>`
              },
            }}
          >
            {IMAGES.map(({ desktopSrc, tabletSrc, phoneSrc }) => (
              <SwiperSlide key={desktopSrc} style={{ width: '100%' }}>
                <AnimationImage src={isPhone ? phoneSrc : isTablet ? tabletSrc : desktopSrc} />
              </SwiperSlide>
            ))}
          </Swiper>
        </SliderContent>
      </SliderContainer>
    </Screen>
  )
}
